const DEV_BACKEND_ORIGIN = 'http://localhost:3001';

let csrfTokenPromise = null;

export function getApiOrigin() {
  const configured = String(process.env.REACT_APP_API_BASE || '').trim().replace(/\/$/, '');
  if (configured) {
    return configured.endsWith('/api') ? configured.slice(0, -4) : configured;
  }

  if (typeof window === 'undefined') {
    return DEV_BACKEND_ORIGIN;
  }

  if (window.location.port === '3000') {
    return DEV_BACKEND_ORIGIN;
  }

  return '';
}

export function getApiBase() {
  const origin = getApiOrigin();
  return origin ? `${origin}/api` : '/api';
}

export function buildApiUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const apiOrigin = getApiOrigin();
  if (normalizedPath.startsWith('/api/') || normalizedPath === '/api') {
    return apiOrigin ? `${apiOrigin}${normalizedPath}` : normalizedPath;
  }
  if (normalizedPath.startsWith('/auth/') || normalizedPath === '/auth') {
    return apiOrigin ? `${apiOrigin}${normalizedPath}` : normalizedPath;
  }
  if (['/check-auth', '/healthz', '/db-healthz', '/storage-healthz'].includes(normalizedPath)) {
    return apiOrigin ? `${apiOrigin}${normalizedPath}` : normalizedPath;
  }
  return apiOrigin ? `${apiOrigin}${normalizedPath}` : normalizedPath;
}

function shouldAttachJsonContentType(body, headers = {}) {
  if (body === undefined || body === null) return false;
  if (body instanceof FormData) return false;
  return !Object.keys(headers).some((key) => key.toLowerCase() === 'content-type');
}

export function isUnsafeMethod(method) {
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(String(method || 'GET').toUpperCase());
}

export async function fetchCsrfToken(force = false) {
  if (!force && csrfTokenPromise) return csrfTokenPromise;

  csrfTokenPromise = (async () => {
    const response = await fetch(buildApiUrl('/auth/csrf-token'), {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload.csrfToken) {
      const error = new Error(payload.message || `Failed to load CSRF token (HTTP ${response.status})`);
      error.status = response.status;
      throw error;
    }
    return payload.csrfToken;
  })();

  try {
    return await csrfTokenPromise;
  } catch (error) {
    csrfTokenPromise = null;
    throw error;
  }
}

export function clearCachedCsrfToken() {
  csrfTokenPromise = null;
}

export function normalizeApiError(status, payload, fallbackText = '', requestPath = '') {
  const rawMessage =
    payload?.message ||
    payload?.error ||
    (fallbackText && !fallbackText.includes('<!DOCTYPE html>') ? fallbackText : '') ||
    `HTTP ${status}`;

  let message = rawMessage;
  const normalizedPath = String(requestPath || '').split('?')[0];
  const isLoginRequest = normalizedPath === '/auth/login';
  const isAdminProtectedRoute =
    normalizedPath.startsWith('/auth/admin/') ||
    normalizedPath.startsWith('/api/admin/');
  if (status === 401 && !isLoginRequest) message = 'Your session expired. Please log in again.';
  if (status === 403 && payload?.code === 'CSRF_INVALID') message = 'Your session security token expired. Refresh and try again.';
  if (status === 403 && isAdminProtectedRoute) message = 'Permission denied.';
  if (status === 413) message = 'Upload too large for this deployment.';
  if (status === 404 && fallbackText.includes('<!DOCTYPE html>')) message = 'Backend route not found or not deployed yet.';
  if (status >= 500 && /ML runner/i.test(String(rawMessage))) message = 'ML unavailable in this deployment.';

  const error = new Error(message);
  error.status = status;
  error.payload = payload;
  return error;
}

export async function apiFetch(
  path,
  {
    method = 'GET',
    headers = {},
    body,
    responseType = 'json',
    signal,
    credentials = 'include',
    skipCsrf = false,
    keepalive = false,
  } = {}
) {
  const finalHeaders = { Accept: 'application/json', ...headers };
  const finalMethod = String(method || 'GET').toUpperCase();
  const finalBody =
    body !== undefined && body !== null && !(body instanceof FormData) && typeof body !== 'string'
      ? JSON.stringify(body)
      : body;

  if (shouldAttachJsonContentType(finalBody, finalHeaders)) {
    finalHeaders['Content-Type'] = 'application/json';
  }

  if (isUnsafeMethod(finalMethod) && !skipCsrf) {
    const csrfToken = await fetchCsrfToken();
    finalHeaders['X-CSRF-Token'] = csrfToken;
  }

  const response = await fetch(buildApiUrl(path), {
    method: finalMethod,
    credentials,
    keepalive,
    headers: finalHeaders,
    body: finalBody,
    signal,
  });

  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await response.json().catch(() => ({})) : null;
  const fallbackText = isJson ? '' : await response.text().catch(() => '');

  if (!response.ok) {
    throw normalizeApiError(response.status, payload, fallbackText, path);
  }

  if (responseType === 'text') {
    return isJson ? JSON.stringify(payload) : fallbackText;
  }

  if (responseType === 'raw') {
    return response;
  }

  if (isJson) return payload;

  try {
    return JSON.parse(fallbackText);
  } catch {
    return fallbackText;
  }
}
